import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTheme } from '../hooks/useTheme';
import {
    TabContent,
    TabPane,
    Nav,
    NavItem,
    NavLink
} from 'reactstrap';
import { get } from '../hooks/apiFetch';
import PlannedResultsTab from './PlannedResultsTab';
import ProcessingResultsTab from './ProcessingResultsTab';
import SuccessfulResultsTab from './SuccessfulResultsTab';
import FailedResultsTab from './FailedResultsTab';
import { getFileIcon } from '../../helpers/Utils/FileIcons';
import '../../ai/Shared.css';
import './UserResults.css';

export const UserResults = (props) => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('successful');
    const [plannedItems, setPlannedItems] = useState([]);
    const [processingItems, setProcessingItems] = useState([]);
    const [successfulItems, setSuccessfulItems] = useState([]);
    const [failedItems, setFailedItems] = useState([]);

    const [loadingPlanned, setLoadingPlanned] = useState(false);
    const [loadingProcessing, setLoadingProcessing] = useState(false);
    const [loadingSuccess, setLoadingSuccess] = useState(false);
    const [loadingFailed, setLoadingFailed] = useState(false);

    const [errorPlanned, setErrorPlanned] = useState(null);
    const [errorProcessing, setErrorProcessing] = useState(null);
    const [errorSuccess, setErrorSuccess] = useState(null);
    const [errorFailed, setErrorFailed] = useState(null);

    const fetchPlanned = async () => {
        setLoadingPlanned(true);
        setErrorPlanned(null);
        try {
            const data = await get('/api/OperationInfo/PlannedResults');

            const formattedUserResults = data.map(item => ({
                id: item.inputId,
                inputIdentifier: item.inputIdentifier,
                originalFileNames: item.originalFileNames,
                utility: item.utilityTypeName || 'Неизвестная утилита',
                date: item.date ? new Date(item.date) : new Date(),
                promptGuid: item.promptGuid,
                promptName: item.promptName,
                role: item.role,
                roleDisplayName: item.roleDisplayName,
                creator: item.creatorFullName,
                plannedStartTime: item.plannedStartTime,
                comment: item.comment,
            }));

            setPlannedItems(formattedUserResults);
        } catch (error) {
            let errorMessage = '';
            if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
                errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage = error.message || 'Ошибка авторизации. Проверьте данные или попробуйте повторить попытку позже';
            }
            setErrorPlanned(errorMessage || 'Произошла ошибка при загрузке запланированных обработок');
            console.error('Ошибка при загрузке запланированных обработок:', error);
        }
        finally {
            setLoadingPlanned(false);
        }
    };

    const fetchProcessing = async () => {
        setLoadingProcessing(true);
        setErrorProcessing(null);
        try {
            const data = await get('/api/OperationInfo/ProcessingResults');

            const formattedUserResults = data.map(item => ({
                id: item.inputId,
                inputIdentifier: item.inputIdentifier,
                originalFileNames: item.originalFileNames,
                utilityType: item.utilityType || "none",
                utility: item.utilityTypeName || 'Неизвестная утилита',
                date: item.date ? new Date(item.date) : new Date(),
                promptGuid: item.promptGuid,
                promptName: item.promptName,
                role: item.role,
                roleDisplayName: item.roleDisplayName,
                creator: item.creatorFullName,
                comment: item.comment,
            }));

            setProcessingItems(formattedUserResults);
        } catch (error) {
            let errorMessage = '';
            if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
                errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage = error.message || 'Ошибка авторизации. Проверьте данные или попробуйте повторить попытку позже';
            }
            setErrorProcessing(errorMessage || 'Произошла ошибка при загрузке выполняющихся обработок');
            console.error('Ошибка при загрузке неудачных обработок:', error);
        }
        finally {
            setLoadingProcessing(false);
        }
    };

    const fetchSuccess = async () => {
        setLoadingSuccess(true);
        setErrorSuccess(null);
        try {
            const data = await get('/api/OperationInfo/SuccessResults');

            const formattedUserResults = data.map(item => ({
                id: item.inputId,
                inputIdentifier: item.inputIdentifier,
                resultFileNames: item.resultFileNames,
                originalFileNames: item.originalFileNames,
                utility: item.utilityTypeName || 'Неизвестная утилита',
                date: item.date ? new Date(item.date) : new Date(),
                lastModificatedDate: item.lastModificatedDate ? new Date(item.lastModificatedDate) : new Date(),
                hasUnreadChanges: item.hasUnreadChanges,
                promptGuid: item.promptGuid,
                promptName: item.promptName,
                role: item.role,
                roleDisplayName: item.roleDisplayName,
                creator: item.creatorFullName,
                comment: item.comment,
                teamMembers: item.teamMembers || []
            }));

            setSuccessfulItems(formattedUserResults);
        } catch (error) {
            let errorMessage = '';
            if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
                errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage = error.message || 'Ошибка авторизации. Проверьте данные или попробуйте повторить попытку позже';
            }
            setErrorSuccess(errorMessage || 'Произошла ошибка при загрузке успешных обработок');
            console.error('Ошибка при загрузке успешных обработок:', error);
        }
        finally {
            setLoadingSuccess(false);
        }
    };

    const fetchFailed = async () => {
        setLoadingFailed(true);
        setErrorFailed(null);
        try {
            const data = await get('/api/OperationInfo/FailedResults');

            const formattedFailedResults = data.map(item => ({
                id: item.inputId,
                inputIdentifier: item.inputIdentifier,
                originalFileNames: item.originalFileNames,
                utility: item.utilityTypeName || 'Неизвестная утилита',
                date: item.date ? new Date(item.date) : new Date(),
                role: item.role,
                roleDisplayName: item.roleDisplayName,
                creator: item.creatorFullName,
                promptGuid: item.promptGuid,
                promptName: item.promptName,
                errorMessage: item.error || 'Ошибка во время выполнения обработки'
            }));

            setFailedItems(formattedFailedResults);
        } catch (error) {
            let errorMessage = '';
            if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
                errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage = error.message || 'Ошибка авторизации. Проверьте данные или попробуйте повторить попытку позже';
            }
            setErrorFailed(errorMessage || 'Произошла ошибка при загрузке неудачных обработок');
            console.error('Ошибка при загрузке неудачных обработок:', error);
        }
        finally {
            setLoadingFailed(false);
        }
    };

    const fetchAll = () => {
        Promise.all([fetchPlanned(), fetchProcessing(), fetchSuccess(), fetchFailed(),]);
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const toggleTab = (tabId) => {
        if (activeTab !== tabId) {
            setActiveTab(tabId);
        }
    };

    return (
        <>
            <Helmet>
                <title>Мои обработки</title>
            </Helmet>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Мои обработки</h4>
            </div>

            {/* Навигация по вкладкам */}
            <Nav tabs className="results-nav-tabs">
                <NavItem>
                    <NavLink
                        className={`results-nav-link ${activeTab === 'planned' ? 'active' : ''}`}
                        onClick={() => toggleTab('planned')}
                    >
                        <span className="tab-title">
                            Запланированные ({plannedItems.length})
                        </span>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={`results-nav-link ${activeTab === 'processing' ? 'active' : ''}`}
                        onClick={() => toggleTab('processing')}
                    >
                        <span className="tab-title">
                            Выполняющиеся ({processingItems.length})
                        </span>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={`results-nav-link ${activeTab === 'successful' ? 'active' : ''}`}
                        onClick={() => toggleTab('successful')}
                    >
                        <span className="tab-title">
                            Успешные ({successfulItems.length})
                        </span>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={`results-nav-link ${activeTab === 'failed' ? 'active' : ''}`}
                        onClick={() => toggleTab('failed')}
                    >
                        <span className="tab-title">
                            Неудачные ({failedItems.length})
                        </span>
                    </NavLink>
                </NavItem>
            </Nav>

            {/* Контент вкладок */}
            <TabContent activeTab={activeTab} className="mt-3">
                <TabPane tabId="planned">
                    {activeTab === 'planned' && (
                        <PlannedResultsTab
                            plannedItems={plannedItems}
                            setPlannedItems={setPlannedItems}
                            loading={loadingPlanned}
                            error={errorPlanned}
                            fetchPlanned={fetchPlanned}
                            history={props.history}
                            getFileIcon={(fileName) => getFileIcon(fileName, theme)}
                        />
                    )}
                </TabPane>
                <TabPane tabId="processing">
                    {activeTab === 'processing' && (
                        <ProcessingResultsTab
                            processingItems={processingItems}
                            setProcessingItems={setProcessingItems}
                            loading={loadingProcessing}
                            error={errorProcessing}
                            fetchProcessing={fetchProcessing}
                            history={props.history}
                            getFileIcon={(fileName) => getFileIcon(fileName, theme)}
                        />
                    )}
                </TabPane>
                <TabPane tabId="successful">
                    {activeTab === 'successful' && (
                        <SuccessfulResultsTab
                            successfulItems={successfulItems}
                            setSuccessfulItems={setSuccessfulItems}
                            loading={loadingSuccess}
                            error={errorSuccess}
                            fetchSuccess={fetchSuccess}
                            history={props.history}
                            getFileIcon={(fileName) => getFileIcon(fileName, theme)}
                        />
                    )}
                </TabPane>
                <TabPane tabId="failed">
                    {activeTab === 'failed' && (
                        <FailedResultsTab
                            failedItems={failedItems}
                            setFailedItems={setFailedItems}
                            loading={loadingFailed}
                            error={errorFailed}
                            fetchFailed={fetchFailed}
                            history={props.history}
                            getFileIcon={(fileName) => getFileIcon(fileName, theme)}
                        />
                    )}
                </TabPane>
            </TabContent>
        </>
    );
};